/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {ErrorType, ResponseData} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, MouseEventHandler, useContext, useImperativeHandle, useState} from 'react';
import Invoice from '../../../models/invoiceModel';
import {trackPlatformAction} from '../../../util/analytics';
import {ActivateHandle, UnlayerDesign} from '../../../util/interfaces';
import {createErrorMessage} from '../../../util/utils';
import SendInvoice from './sendInvoice';
import {AppContext} from '../../misc/appContext';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    onSuccess: (invoice: Invoice) => void;
    onDownloadInvoice: (invoice: Invoice) => MouseEventHandler;
    onAssignInvoiceNumber: (invoice: Invoice) => MouseEventHandler;
}

function SendInvoiceController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const {onSuccess, onDownloadInvoice, onAssignInvoiceNumber} = props;
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [invoice, setInvoice] = useState<Invoice | null>(null);
    const [subject, setSubject] = useState('');
    const [error, setError] = useState<ErrorType>(null);

    function onActivate(invoice: Invoice) {
        setInvoice(invoice);
        setActive(true);
        setSubject(`${t('invoice.send.subjectPrefix', 'Factuur voor bestelling')} ${invoice.orderId}`);
    }

    function onClose() {
        setActive(false);
        setLoading(false);
        setInvoice(null);
        setSubject('');
    }

    function onChangeSubject(value: string) {
        setSubject(value);
        setError(null);
    }

    function onSubmit(data: {design: UnlayerDesign; html: string}) {
        if (!validateValues(data.html)) return;
        if (!invoice) return;
        setLoading(true);
        invoice.sendConfigured(
            data.html,
            data.design,
            subject,
            (invoice: Invoice, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    trackPlatformAction('invoices', 'send_invoice');
                    saveToContext(data.design);
                    onSuccess(invoice);
                    onClose();
                    showSnackbar(t('invoice.send.sentSuccess', 'De factuur is verzonden'));
                } else onSendingFailed(data);
            }
        );
    }

    function onSendingFailed(data: Record<string, any>) {
        const {error_type, message, order_id} = data;
        if (error_type === 'email_not_available') {
            showDialog(
                <label>
                    {t(
                        'invoice.send.emailNotAvailableBefore',
                        'Het originele e-mailadres van de klant is niet meer beschikbaar voor Bluemate. We raden aan om de factuur te'
                    )}{' '}
                    <span className='invoice-download-link' onClick={_onDownloadInvoice}>
                        {t('invoice.send.download', 'downloaden')}
                    </span>{' '}
                    {t(
                        'invoice.send.emailNotAvailableAfter',
                        'en deze via het Bol-platform te verzenden naar de klant.'
                    )}
                </label>,
                t('invoice.send.failedTitle', 'Verzenden mislukt')
            );
        } else if (error_type === 'invoice_num_not_set') {
            if (invoice)
                showDialog(
                    t(
                        'invoice.send.invoiceNumNotSet',
                        'Deze factuur heeft nog geen factuurnummer en kan daarom niet verzonden worden. Het systeem kan geen factuurnummer bepalen voor orders die ouder zijn dan jouw Bluemate account. Voor deze orders dien je dus zelf het factuurnummer in te stellen.'
                    ),
                    t('invoice.send.setInvoiceNumTitle', 'Factuurnummer instellen'),
                    [
                        {
                            label: t('invoice.send.setInvoiceNumTitle', 'Factuurnummer instellen'),
                            onClick: _onAssignInvoiceNumber
                        },
                        {label: t('common.close', 'Sluiten'), primary: !invoice}
                    ]
                );
            else
                showDialog(
                    `${t('invoice.send.orderPrefix', 'Order')} ${order_id} ${t(
                        'invoice.send.invoiceNumNotSetPlural',
                        'heeft nog geen factuurnummer en kan daarom niet verzonden worden. Het systeem kan geen factuurnummer bepalen voor orders die ouder zijn dan jouw Bluemate account. Voor deze orders dien je dus zelf het factuurnummer in te stellen.'
                    )}`,
                    t('invoice.send.setInvoiceNumTitle', 'Factuurnummer instellen')
                );
        } else
            showDialog(
                createErrorMessage(
                    message ?? t('invoice.send.sendFailed', 'Het verzenden van de factuur is niet gelukt.')
                ),
                t('invoice.send.failedTitle', 'Verzenden mislukt')
            );
    }

    function _onDownloadInvoice() {
        onDownloadInvoice(invoice!);
        onClose();
    }

    function _onAssignInvoiceNumber(evt: React.MouseEvent) {
        onAssignInvoiceNumber(invoice!)(evt);
        onClose();
    }

    function saveToContext(design: UnlayerDesign) {
        const {account} = context.accountContext;
        if (!account) return;
        const settings = account.settings?.clone();
        if (!settings) return;
        settings.manualMailJson = design;
        account.settings = settings;
        context.onChangeContext({account});
    }

    function validateValues(mailText: string): boolean {
        if (!subject) {
            setError(t('invoice.send.errorSubject', 'Vul het onderwerp in'));
            return false;
        } else if (!mailText) {
            setError(t('invoice.send.errorText', 'Vul de tekst van de e-mail in'));
            return false;
        }
        return true;
    }

    useImperativeHandle(ref, () => ({
        activate: onActivate
    }));

    return (
        <SendInvoice
            onClose={onClose}
            onSubmit={onSubmit}
            onChangeSubject={onChangeSubject}
            active={active}
            subject={subject}
            loading={loading}
            invoice={invoice}
            error={error}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(SendInvoiceController);
